/**
 * The rows of figure S6's "Portfolio entities" table, derived once.
 *
 * Pure: no React, no fetching. The page, the table and the CSV export all read
 * the SAME rows, so what a portfolio downloads is exactly what it was looking
 * at — a CSV that disagreed with the screen above it would be worse than none.
 */
import type { AggregatorProject } from '../../types'
import type { EntityTags } from '../../hooks/useAggregatorPreferences'
import type { EntityGroup } from './entityGroups'
import { formatLastActivity } from './entityGroups'
import { groupSharing, sharingLines } from './sharing'
import type { EntitySharing } from './sharing'
import { intakeForGroup } from './intake'
import type { IntakeTags } from './intake'
import { LIVE_STATUSES, kindLabel } from '../sharing/ipModel'
import type { IpArrangementHolderRow } from '../../types/ipArrangements'

/** What the Stage cell prints when no stage is recorded for an entity. */
export const NO_STAGE = '—'

/**
 * Why the Stage cell is empty. Round 2 put this in a hover title only, so a
 * reader of the page saw six unexplained dashes; it is now printed under the
 * table, beside the legend, where it can actually be read.
 */
export const NO_STAGE_REASON =
  'No stage is recorded for this entity. A stage is set by this portfolio on the entity, not by the inventor.'

/** The same fact as a footnote under the table, where every dash can see it. */
export const NO_STAGE_FOOTNOTE =
  '— means no stage is recorded: a stage is set by this portfolio on the entity, never by the inventor.'

/**
 * The stages figure S6's filter offers. These are FUNDING stages, and nothing
 * on the platform stores one — the only stage anything records is the
 * tech-transfer stage a portfolio tags an entity with (constants/aggregator's
 * STAGE_OPTIONS). The filter offers these three anyway, as the figure does,
 * unioned with every stage really recorded, so it is a working control on real
 * data and an honest empty result on a stage nothing carries.
 */
export const FIGURE_STAGES = ['Pre-seed', 'Seed', 'Series A']

/**
 * What an entity's IP looks like from this portfolio's side: the kind of the
 * live arrangement on record, or the default. "Inventor retains" is not an
 * absence — it is the ordinary, correct state of an invention, and the figure
 * names it.
 */
export const IP_RETAINED = 'Inventor retains'

/** The IP postures figure S6's third filter offers. */
export const FIGURE_IP_POSTURES = [IP_RETAINED, 'Option', 'Licence']

export interface PortfolioEntityRow {
  key: string
  label: string
  group: EntityGroup
  sharing: EntitySharing
  /** The portfolio's own intake label for this entity, when it set one. */
  intake: string | null
  /**
   * The stage this portfolio has recorded against the entity, or null.
   *
   * It used to summarise each project's `status` — which is a storage value
   * ('active') and not a stage at all, so every row printed "Active" under a
   * column headed Stage. Nothing on the platform records a FUNDING stage
   * (there is no such field on an entity or a project), and the only stage
   * anything records is the one the portfolio itself sets on an entity. So
   * that is what this reads, and where none is set the cell says so.
   */
  stage: string | null
  /** The vertical most of this entity's inventions sit in, when they carry one. */
  vertical: string | null
  /**
   * The entity group this row's owners are filed under — the same grouping
   * buildEntityGroups computes for /agg/entity/group/:groupName. Null when it
   * is simply the row's own name, which is the ordinary case: printing the
   * name twice tells the reader nothing.
   */
  parentGroup: string | null
  /**
   * "Inventor retains" unless a live IP arrangement is on record against one of
   * this entity's granted projects, in which case its kind — Option, Licence or
   * Assignment. Read from the holder's own route, which returns counts only.
   */
  ipPosture: string
  projectCount: number
  /** ISO timestamp of the most recently updated granted project. */
  lastActivity?: string
}

/**
 * The stage this portfolio has set on the entity. A group carries one only
 * when its owners agree — two owners tagged differently have no single answer,
 * and inventing one would be a false label.
 */
export function stageForGroup(tags: EntityTags, entityIds: string[]): string | null {
  const stages = new Set(
    entityIds.map((id) => tags[id]?.stage).filter((s): s is NonNullable<typeof s> => !!s),
  )
  return stages.size === 1 ? [...stages][0] : null
}

/**
 * The IP posture for one group: the kind of the newest live arrangement across
 * its owners, or "Inventor retains" when none is on record. A declined,
 * disputed or superseded record is history and never sets the posture.
 */
export function ipPostureForGroup(
  arrangementsByEntity: Map<string, IpArrangementHolderRow[]>,
  entityIds: string[],
): string {
  const live = entityIds
    .flatMap((id) => arrangementsByEntity.get(id) ?? [])
    .filter((a) => LIVE_STATUSES.has(a.status))
  if (live.length === 0) return IP_RETAINED
  const newest = [...live].sort(
    (a, b) => (b.proposed_at ?? '').localeCompare(a.proposed_at ?? ''),
  )[0]
  return kindLabel(newest.kind)
}

/**
 * The group name to print UNDER the entity name, or null when there is nothing
 * to say. A row is a group, so the group's own label is already the heading;
 * this is only non-null when the owners' own recorded names differ from it —
 * which is what a portfolio's entity-group override produces, and is the one
 * case where a reader needs telling which group they are looking at.
 */
export function parentGroupOf(group: EntityGroup): string | null {
  const recorded = new Set(
    group.members.map((m) => m.entity_name).filter((n): n is string => !!n),
  )
  if (recorded.size === 0) return null
  if (recorded.size === 1 && [...recorded][0] === group.label) return null
  return group.label
}

/**
 * The sub-line under an entity's name on figure S6: the group it is filed
 * under, and the IP posture when there is an arrangement on record.
 *
 * NOT the intake, and not the vertical. Round 4 printed "Intake 2 · cleantech"
 * on every row of an Intake-2-filtered table, which repeats the filter's own
 * value once per row for no information at all. And "Inventor retains" is the
 * default every row would carry, so it is left off: the sub-line names what is
 * unusual about a row, never what is true of all of them.
 */
export function entitySubLine(row: PortfolioEntityRow): string | null {
  const parts: string[] = []
  if (row.parentGroup) parts.push(row.parentGroup)
  if (row.ipPosture && row.ipPosture !== IP_RETAINED) {
    parts.push(`IP: ${row.ipPosture.toLowerCase()}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/** The vertical most of a group's projects carry; null when none do. */
export function dominantVertical(projects: AggregatorProject[]): string | null {
  const counts = new Map<string, number>()
  for (const p of projects) {
    const v = (p.vertical ?? '').trim()
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  if (counts.size === 0) return null
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0]
}

function latestUpdate(projects: AggregatorProject[]): string | undefined {
  return projects.reduce<string | undefined>((acc, p) => {
    if (!p.updated_at) return acc
    if (!acc) return p.updated_at
    return new Date(p.updated_at).getTime() > new Date(acc).getTime() ? p.updated_at : acc
  }, undefined)
}

export function buildPortfolioEntityRows(
  groups: EntityGroup[],
  intakeTags: IntakeTags,
  entityTags: EntityTags = {},
  arrangementsByEntity: Map<string, IpArrangementHolderRow[]> = new Map(),
): PortfolioEntityRow[] {
  return groups.map((group) => {
    const memberIds = group.members.map((m) => m.entity_id)
    return {
      key: group.key,
      label: group.label,
      group,
      sharing: groupSharing(group),
      intake: intakeForGroup(intakeTags, memberIds),
      stage: stageForGroup(entityTags, memberIds),
      vertical: dominantVertical(group.projects),
      // The group is the row here, so this only says something when a member's
      // own recorded name differs from the group it is filed under — which is
      // exactly what an entity-group override produces.
      parentGroup: parentGroupOf(group),
      ipPosture: ipPostureForGroup(arrangementsByEntity, memberIds),
      projectCount: group.projects.length,
      lastActivity: latestUpdate(group.projects),
    }
  })
}

/**
 * An entity name split so the ★ can be tied to its LAST word.
 *
 * Three rounds of the gate went into this one mark. Round 4 let it wrap onto a
 * line of its own. Round 5 fixed that by making the whole cell nowrap, and the
 * longest name was then truncated inside a cell that still had slack — and the
 * figure never truncates an entity name. Round 6 bound the mark with a NO-BREAK
 * SPACE, which is the typographic answer and IS emitted, but the browser still
 * broke there: the mark is an inline-block, and an inline-block boundary is a
 * break opportunity of its own that U+00A0 in the text run does not close.
 *
 * So the name wraps anywhere a name may wrap EXCEPT before its last word, and
 * only that last word and the mark are held together. Nothing is truncated and
 * the mark cannot be orphaned.
 */
export function splitLastWord(label: string): { head: string; last: string } {
  const trimmed = label.trim()
  const at = trimmed.lastIndexOf(' ')
  if (at === -1) return { head: '', last: trimmed }
  return { head: trimmed.slice(0, at), last: trimmed.slice(at + 1) }
}

/** The orderings figure S6's first select offers. */
export type RowOrder = 'entity' | 'intake' | 'vertical' | 'stage' | 'date' | 'status'

export const ROW_ORDER_LABEL: Record<RowOrder, string> = {
  entity: 'By Entity',
  intake: 'By intake',
  vertical: 'By Vertical',
  stage: 'By Stage',
  date: 'By Date',
  status: 'By Status',
}

export const ROW_ORDERS: RowOrder[] = ['entity', 'intake', 'vertical', 'stage', 'date', 'status']

/** How the Sharing column sorts: shared first, then invited, then not accepted. */
const STATUS_RANK: Record<EntitySharing['state'], number> = {
  'by-invitation': 0,
  'joined-by-sharing': 1,
  'invited-not-accepted': 2,
}

/**
 * A row with no value for the field being ordered on sorts LAST, whatever the
 * locale would do with a sentinel string — an entity the portfolio has not
 * labelled yet belongs at the bottom of "By intake", not scattered through it.
 */
function nullsLast(
  a: string | null,
  b: string | null,
  compare: (x: string, y: string) => number,
): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return compare(a, b)
}

export function orderRows(rows: PortfolioEntityRow[], order: RowOrder): PortfolioEntityRow[] {
  const byLabel = (a: PortfolioEntityRow, b: PortfolioEntityRow) => a.label.localeCompare(b.label)
  const copy = [...rows]
  switch (order) {
    case 'intake':
      return copy.sort(
        (a, b) =>
          nullsLast(a.intake, b.intake, (x, y) => x.localeCompare(y, 'en-AU', { numeric: true })) ||
          byLabel(a, b),
      )
    case 'vertical':
      return copy.sort(
        (a, b) => nullsLast(a.vertical, b.vertical, (x, y) => x.localeCompare(y)) || byLabel(a, b),
      )
    case 'stage':
      return copy.sort(
        (a, b) => nullsLast(a.stage, b.stage, (x, y) => x.localeCompare(y)) || byLabel(a, b),
      )
    case 'date':
      return copy.sort((a, b) => {
        const at = a.lastActivity ? new Date(a.lastActivity).getTime() : 0
        const bt = b.lastActivity ? new Date(b.lastActivity).getTime() : 0
        return bt - at || byLabel(a, b)
      })
    case 'status':
      return copy.sort((a, b) => STATUS_RANK[a.sharing.state] - STATUS_RANK[b.sharing.state] || byLabel(a, b))
    case 'entity':
    default:
      return copy.sort(byLabel)
  }
}

export interface RowFilters {
  /** Only entities the portfolio brought in under this intake. */
  intake?: string | null
  /** Only entities whose summarised stage matches. */
  stage?: string | null
  /** Only entities at this IP posture. */
  ip?: string | null
}

export function filterRows(rows: PortfolioEntityRow[], filters: RowFilters): PortfolioEntityRow[] {
  return rows.filter((row) => {
    if (filters.intake && row.intake !== filters.intake) return false
    if (filters.stage && row.stage !== filters.stage) return false
    if (filters.ip && row.ipPosture !== filters.ip) return false
    return true
  })
}

/**
 * The stage filter's menu: the figure's three, plus every stage really recorded
 * on these rows. A figure stage nothing carries still selects — and honestly
 * empties the table — rather than being hidden or greyed out.
 */
export function stageOptions(rows: PortfolioEntityRow[]): string[] {
  return [...new Set([...FIGURE_STAGES, ...stagesPresent(rows)])]
}

/** Every stage really recorded on these rows. */
export function stagesPresent(rows: PortfolioEntityRow[]): string[] {
  return [...new Set(rows.map((r) => r.stage).filter((s): s is string => !!s))]
    .sort((a, b) => a.localeCompare(b))
}

/** The IP filter's menu: the figure's three, plus any kind really on record. */
export function ipPostureOptions(rows: PortfolioEntityRow[]): string[] {
  return [...new Set([...FIGURE_IP_POSTURES, ...rows.map((r) => r.ipPosture)])]
}

const QUOTE = String.fromCharCode(34)

function csvCell(value: string): string {
  // Tested with includes() rather than a character class: a regex literal
  // holding a quotation mark reads as an unterminated string to a naive
  // source scanner, and the repository's vocabulary gate is one.
  const needsQuoting = value.includes(',') || value.includes('\n') || value.includes(QUOTE)
  return needsQuoting ? `${QUOTE}${value.split(QUOTE).join(QUOTE + QUOTE)}${QUOTE}` : value
}

/**
 * The same five columns the table shows, in the same order, for the header's
 * Export CSV. Sharing is exported as the two lines the cell prints, joined —
 * never as a licensing word, because that is not what the column means.
 */
export function rowsToCsv(rows: PortfolioEntityRow[]): string {
  const header = [
    'Entity', 'By invitation', 'Intake', 'Stage', 'IP posture', 'Projects', 'Sharing', 'Last activity',
  ]
  const lines = rows.map((row) => {
    const lines2 = sharingLines(row.sharing)
    const sharing = [lines2.primary, lines2.secondary].filter(Boolean).join(' · ')
    return [
      row.label,
      row.sharing.byInvitation ? 'yes' : 'no',
      row.intake ?? '',
      row.stage ?? '',
      row.ipPosture,
      String(row.projectCount),
      sharing,
      formatLastActivity(row.lastActivity) ?? 'No activity',
    ].map(csvCell).join(',')
  })
  return [header.join(','), ...lines].join('\n')
}
